import axiosClient from './axiosClient';
import { TicketModel } from '../Models/ticketModel';
import projectService from './projectService';
import ticketStatusService from './ticketStatusService';
import ticketStatusHistoryService from './ticketStatusHistoryService';
import ticketAssignmentService from './ticketAssignmentService';

// Former demo fallbacks. Statuses, severities and environments are configured
// per project in the API, so pages must use the real lists; these stay empty
// only so existing "|| mockX" fallbacks resolve to nothing instead of fake names.
export const mockSeverities = [];
export const mockStatuses = [];
export const mockEnvironments = [];

const ticketService = {
  // ---------- Ticket_TBL ----------
  getAll: (params = {}) => {
    // Map query parameter casings
    const requestParams = {};
    if (params.projectID || params.projectId) requestParams.projectId = Number(params.projectID || params.projectId);
    if (params.statusID || params.statusId) requestParams.statusId = Number(params.statusID || params.statusId);
    if (params.severityID || params.severityId) requestParams.severityId = Number(params.severityID || params.severityId);
    if (params.search) requestParams.search = params.search;

    return axiosClient.get('/Ticket', { params: requestParams })
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("GET /Ticket returned 404. Falling back to GET /tickets...");
          return axiosClient.get('/tickets', { params: requestParams });
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map(TicketModel.toFrontend);
        return {
          data: mappedList,
        };
      });
  },

  getById: (ticketID) => {
    return axiosClient.get(`/Ticket/${ticketID}`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn(`GET /Ticket/${ticketID} returned 404. Falling back to GET /tickets/${ticketID}...`);
          return axiosClient.get(`/tickets/${ticketID}`);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketModel.toFrontend(raw),
        };
      });
  },

  create: async (payload) => {
    // Determine the clientOrgID from the selected project
    let clientOrgID = payload.clientOrgID;
    if (!clientOrgID && payload.projectID) {
      try {
        const projRes = await projectService.getById(payload.projectID);
        if (projRes?.data) {
          clientOrgID = projRes.data.clientOrgID;
        }
      } catch (e) {
        console.error("Failed to fetch project for clientOrgID:", e);
      }
    }

    // Determine the first/default statusId configured for the selected project
    let statusID = payload.statusID || payload.statusId;
    if (payload.projectID) {
      try {
        const statusRes = await ticketStatusService.getAll();
        if (statusRes?.data) {
          const projStats = statusRes.data.filter(s => Number(s.projectID) === Number(payload.projectID));
          if (projStats.length > 0) {
            // Sort by sequenceOrder to find the first one (lowest sequence)
            const sorted = [...projStats].sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
            statusID = sorted[0].statusID;
          }
        }
      } catch (e) {
        console.error("Failed to fetch default status for project:", e);
      }
    }

    // Get logged-in user ID
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {
      console.error("Failed to parse user from local storage:", e);
    }

    const body = TicketModel.toBackend({
      ...payload,
      clientOrgID: clientOrgID,
      statusID: statusID || payload.statusID,
      raisedBy: loggedInUserId,
      lastUpdatedBy: loggedInUserId,
    });
    body.ticketId = 0;
    // Ticket number is generated by the API from the project code (e.g. PROJ-101)
    body.ticketNumber = null;
    // Answers to the project's client questions (form fields clientAnswer_<questionID>)
    body.clientAnswers = Object.keys(payload)
      .filter((key) => key.startsWith('clientAnswer_') && String(payload[key] || '').trim())
      .map((key) => ({ questionId: Number(key.replace('clientAnswer_', '')), answerText: String(payload[key]).trim() }));

    return axiosClient.post('/Ticket', body)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("POST /Ticket returned 404. Falling back to POST /tickets...");
          return axiosClient.post('/tickets', body);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketModel.toFrontend(raw),
        };
      });
  },

  update: async (ticketID, payload) => {
    // Get logged-in user ID
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {
      console.error("Failed to parse user from local storage:", e);
    }

    let mergedPayload = { ...payload };
    if (!payload.projectID && !payload.projectId) {
      try {
        const existing = await ticketService.getById(ticketID);
        if (existing?.data) {
          mergedPayload = {
            ...existing.data,
            ...payload,
          };
        }
      } catch (err) {
        console.warn("Could not retrieve ticket before update:", err);
      }
    }

    // Determine the clientOrgID from the selected project (if not present)
    let clientOrgID = mergedPayload.clientOrgID || mergedPayload.clientOrgId;
    if (!clientOrgID && (mergedPayload.projectID || mergedPayload.projectId)) {
      try {
        const projRes = await projectService.getById(mergedPayload.projectID || mergedPayload.projectId);
        if (projRes?.data) {
          clientOrgID = projRes.data.clientOrgID || projRes.data.clientOrgId;
        }
      } catch (e) {
        console.error("Failed to fetch project for clientOrgID:", e);
      }
    }

    const body = TicketModel.toBackend({
      ...mergedPayload,
      ticketID,
      clientOrgID: clientOrgID || mergedPayload.clientOrgID,
      lastUpdatedBy: loggedInUserId,
    });
    // Notes sent with a status change (saved by the API as a comment)
    if (mergedPayload.statusComment) body.statusComment = mergedPayload.statusComment;

    return axiosClient.put('/Ticket', body)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("PUT /Ticket returned 404. Falling back to PUT /tickets...");
          return axiosClient.put('/tickets', body);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketModel.toFrontend(raw),
        };
      });
  },

  remove: (ticketID) => {
    return axiosClient.delete(`/Ticket/${ticketID}`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn(`DELETE /Ticket/${ticketID} returned 404. Falling back to DELETE /tickets/${ticketID}...`);
          return axiosClient.delete(`/tickets/${ticketID}`);
        }
        return Promise.reject(err);
      });
  },

  changeStatus: async (ticketID, payload) => {
    // The API records status history itself and validates the move
    // (transition rules, mandatory fields, reopen reason).
    return ticketService.update(ticketID, {
      ...payload,
      statusID: payload.toStatusID,
      reopenReasonID: payload.reopenReasonID,
    });
  },

  // Connect sub-resources to flat backend controllers
  getHistory: (ticketID) => {
    return ticketStatusHistoryService.getAll({ ticketID });
  },

  getAssignments: (ticketID) => {
    return ticketAssignmentService.getByTicketId(ticketID);
  },

  assign: async (ticketID, userID, customAssignedBy) => {
    let loggedInUserId = customAssignedBy || 1;
    if (!customAssignedBy) {
      try {
        const userStr = localStorage.getItem('afix_user');
        if (userStr) {
          const user = JSON.parse(userStr);
          loggedInUserId = Number(user.userID || user.userId || user.id || 1);
        }
      } catch {
      // Fall back to the payload as sent
    }
    }

    // Step 1: Check existing active assignments and unassign them to prevent unique constraint conflicts in SQL Server
    try {
      const existingRes = await ticketAssignmentService.getByTicketId(ticketID);
      const activeAssignments = (existingRes?.data || []).filter(
        (a) => a && (a.isCurrentFlag === true || a.isCurrentFlag === 'true' || a.isCurrentFlag === 1)
      );

      // If this user is already active, return the existing assignment
      const alreadyActive = activeAssignments.find(
        (a) => Number(a.userID || a.userId) === Number(userID)
      );
      if (alreadyActive) {
        return { data: alreadyActive };
      }

      // Mark previous assignments as not current
      for (const prev of activeAssignments) {
        if (prev && prev.assignmentID) {
          await ticketAssignmentService.update(prev.assignmentID, {
            ...prev,
            isCurrentFlag: false,
            unassignedDttm: new Date().toISOString(),
          }).catch(() => {
            return ticketAssignmentService.remove(prev.assignmentID).catch(() => {});
          });
        }
      }
    } catch (checkErr) {
      console.warn('Could not deactivate previous assignments:', checkErr);
    }

    // Step 2: Create the new active assignment
    const payload = {
      assignmentId: 0,
      ticketId: Number(ticketID),
      userId: Number(userID),
      assignedBy: Number(loggedInUserId),
      assignedDttm: new Date().toISOString(),
      isCurrentFlag: true,
    };

    return ticketAssignmentService.create(payload);
  },

  updateAssignment: (assignmentID, payload) => {
    return ticketAssignmentService.update(assignmentID, payload);
  },

  unassign: (ticketID, assignmentID) => {
    return ticketAssignmentService.remove(assignmentID);
  },

  getComments: (ticketID) => {
    return axiosClient.get('/TicketComment')
      .catch(() => axiosClient.get(`/tickets/${ticketID}/comments`))
      .then(res => {
        let list = res?.data?.data || res?.data || [];
        if (!Array.isArray(list)) {
          list = list ? [list] : [];
        }
        const filtered = list.filter(c => c && Number(c.ticketId || c.ticketID) === Number(ticketID))
          .map(c => ({
            commentID: c.commentId || c.commentID,
            ticketID: c.ticketId || c.ticketID,
            commentedBy: c.commentedBy,
            commentText: c.commentText,
            isInternalFlag: c.isInternalFlag,
            createdDttm: c.createdDttm || c.createDttm,
            commentedByName: c.commentedByName || `User ${c.commentedBy}`,
          }));
        return { data: filtered };
      })
      .catch(() => ({ data: [] }));
  },

  addComment: (ticketID, payload) => {
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch {
      // Fall back to the payload as sent
    }

    const body = {
      commentId: 0,
      ticketId: Number(ticketID),
      commentedBy: loggedInUserId,
      commentText: payload.commentText,
      isInternalFlag: !!payload.isInternalFlag,
      createdDttm: new Date().toISOString(),
    };

    return axiosClient.post('/TicketComment', body);
  },

  getAttachments: (ticketID) => {
    return axiosClient.get('/TicketAttachment')
      .catch(() => axiosClient.get(`/tickets/${ticketID}/attachments`))
      .then(res => {
        const list = res?.data?.data || res?.data || [];
        const filtered = list.filter(a => Number(a.ticketId || a.ticketID) === Number(ticketID))
          .map(a => ({
            attachmentID: a.attachmentId || a.attachmentID,
            ticketID: a.ticketId || a.ticketID,
            fileName: a.fileName,
            filePath: a.filePath,
            fileSizeKB: a.fileSizeKB,
            fileType: a.fileType,
            virusScanStatus: a.virusScanStatus,
            uploadedBy: a.uploadedBy,
            uploadedDttm: a.uploadedDttm,
          }));
        return { data: filtered };
      })
      .catch(() => ({ data: [] }));
  },

  uploadAttachment: (ticketID, files) => {
    const fileList = Array.isArray(files) ? files : [files];
    const formData = new FormData();
    formData.append('AttachmentId', '0');
    formData.append('TicketId', String(ticketID));
    
    fileList.forEach(file => {
      formData.append('Files', file);
    });

    if (fileList.length > 0) {
      formData.append('FileName', fileList[0].name || 'attachment');
      formData.append('FilePath', fileList[0].name || 'temp'); // satisfy validation
      formData.append('FileSizeKB', String(Math.round(fileList[0].size / 1024)));
      formData.append('FileType', fileList[0].type || 'application/octet-stream');
    } else {
      formData.append('FileName', 'attachment');
      formData.append('FilePath', 'temp');
      formData.append('FileSizeKB', '0');
      formData.append('FileType', 'application/octet-stream');
    }

    formData.append('VirusScanStatus', 'Pending');
    
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch {
      // Fall back to the payload as sent
    }
    formData.append('UploadedBy', String(loggedInUserId));
    formData.append('UploadedDttm', new Date().toISOString());

    return axiosClient.post('/TicketAttachment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  removeAttachment: (ticketID, attachmentID) => {
    return axiosClient.delete(`/TicketAttachment/${attachmentID}`);
  },

  // ---------- Ticket_Link_TBL (Duplicate / Related / Parent-Child, same project only) ----------
  getLinks: (ticketID) =>
    axiosClient.get('/TicketLink').then((res) => {
      const list = res?.data?.data || res?.data || [];
      return {
        data: (Array.isArray(list) ? list : [])
          .map((l) => ({
            linkID: l.linkId ?? l.linkID,
            ticketID: l.ticketId ?? l.ticketID,
            linkedTicketID: l.linkedTicketId ?? l.linkedTicketID,
            linkTypeID: l.linkTypeId ?? l.linkTypeID,
            createdDttm: l.createdDttm,
          }))
          .filter((l) => Number(l.ticketID) === Number(ticketID) || Number(l.linkedTicketID) === Number(ticketID)),
      };
    }),
  addLink: (ticketID, payload) =>
    axiosClient.post('/TicketLink', {
      linkId: 0,
      ticketId: Number(ticketID),
      linkedTicketId: Number(payload.linkedTicketID),
      linkTypeId: Number(payload.linkTypeID),
    }),
  removeLink: (linkID) => axiosClient.delete(`/TicketLink/${linkID}`),

  // ---------- Ticket_Client_Answer_TBL (answers given when the ticket was raised) ----------
  getClientAnswers: (ticketID) =>
    axiosClient.get('/TicketClientAnswer').then((res) => {
      const list = res?.data?.data || res?.data || [];
      return {
        data: (Array.isArray(list) ? list : [])
          .map((a) => ({
            answerID: a.answerId ?? a.answerID,
            ticketID: a.ticketId ?? a.ticketID,
            questionID: a.questionId ?? a.questionID,
            answerText: a.answerText,
          }))
          .filter((a) => Number(a.ticketID) === Number(ticketID)),
      };
    }),
  submitClientAnswers: (ticketID, answers) => Promise.resolve({ data: { success: true } }),
};

export default ticketService;
